import { useMemo } from "react";
import { useCloseTemplates } from "@api/closeTemplate.api";
import { useOrganizationCloseStages } from "@api/organizationCloseStage.api";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { useWorkspacesList } from "@api/workspace.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";
import { useUser } from "@hooks/useUser";
import { useScope } from "@state/scopeContext.lib";
import { getOrderedCloseStages } from "@components/organizationView/clients-work/workStageGroups";
import {
    type DemoClient,
    type DemoNamedRef,
    type DemoPerson,
    toDemoClientsFromWorkspaces,
    toDemoEntities,
    toDemoPerson,
} from "./demoSources";

export interface DemoOrganizationSources {
    isReady: boolean;
    organizationId: string | null;
    /** Every client with a named entity; empty while unknown. */
    clients: readonly DemoClient[];
    people: readonly DemoPerson[];
    /** Stages a close can sit in (ones without sub-stages), in board order. */
    stages: readonly DemoNamedRef[];
    templates: readonly DemoNamedRef[];
}

/** The organization's real clients, team, stages and templates the fakes attach to; fetched only while `isEnabled`. */
export const useDemoOrganizationSources = ({
    isEnabled,
}: {
    isEnabled: boolean;
}): DemoOrganizationSources => {
    const { organizationId = null } = useScope();
    const isOrganizationEnabled = isEnabled && organizationId !== null;
    const workspacesQuery = useWorkspacesList(
        organizationId ? { organizationId, includeEntities: true } : undefined,
        { enabled: isOrganizationEnabled },
    );
    const membersQuery = useCurrentOrganizationMembers({
        enabled: isOrganizationEnabled,
    });
    const stagesQuery = useOrganizationCloseStages({ enabled: isEnabled });
    const templatesQuery = useCloseTemplates({ enabled: isEnabled });
    const isReady =
        isEnabled &&
        [workspacesQuery, membersQuery, stagesQuery, templatesQuery].every(
            (query) => !query.isLoading,
        );

    return useMemo(() => {
        const stages = stagesQuery.data ?? [];
        return {
            isReady,
            organizationId,
            clients: toDemoClientsFromWorkspaces(workspacesQuery.data ?? []),
            people: (membersQuery.data ?? []).map(({ user }) =>
                toDemoPerson({ user, organizationId }),
            ),
            stages: getOrderedCloseStages(stages)
                .filter(({ stage }) =>
                    stages.every(
                        (other) => other.parentCloseStageId !== stage.id,
                    ),
                )
                .map(({ stage }) => ({ id: stage.id, name: stage.name })),
            templates: (templatesQuery.data ?? []).map(({ id, name }) => ({
                id,
                name,
            })),
        };
    }, [
        isReady,
        organizationId,
        workspacesQuery.data,
        membersQuery.data,
        stagesQuery.data,
        templatesQuery.data,
    ]);
};

export interface DemoWorkspaceSources {
    isReady: boolean;
    /** The current workspace as a client, with its real entities; null outside a workspace. */
    client: DemoClient | null;
    people: readonly DemoPerson[];
}

/** The current workspace, its entities and members, for the workspace Actions fakes; fetched only while `isEnabled`. */
export const useDemoWorkspaceSources = ({
    isEnabled,
}: {
    isEnabled: boolean;
}): DemoWorkspaceSources => {
    const { workspaceId = null } = useScope();
    const workspaceName = useActiveWorkspace()?.name ?? null;
    const { data: members, isLoading: areMembersLoading } =
        useCurrentWorkspaceMembers({ enabled: isEnabled });
    const entityContexts = useEntitiesContexts({}, { enabled: isEnabled });
    const isReady =
        isEnabled &&
        !areMembersLoading &&
        workspaceName !== null &&
        entityContexts !== undefined;

    return useMemo(
        () => ({
            isReady,
            client:
                workspaceId === null || workspaceName === null
                    ? null
                    : {
                          workspaceId,
                          name: workspaceName,
                          entities: toDemoEntities(
                              (entityContexts ?? []).map(
                                  ({ entity }) => entity,
                              ),
                          ),
                      },
            people: (members ?? []).map(({ user, organizationId }) =>
                toDemoPerson({ user, organizationId }),
            ),
        }),
        [isReady, workspaceId, workspaceName, entityContexts, members],
    );
};

/** The signed-in user's id, whom some fakes are assigned to; null while unknown. */
export const useDemoSelfId = (): number | null => useUser()?.id ?? null;
